// Topbar usage ring — 28px SVG circular progress with tiny label/value.
// Part 3.1 WhatChimp design language parity (WP-00).
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { fmtCompact, type UsageRingData } from '../../mocks/dashboard';

const RING_COLORS: Record<string, string> = {
  'topbar.subscribers': 'text-brand',
  'topbar.messages': 'text-success',
  'topbar.aiTokens': 'text-violet-500',
};

export default function UsageRing({
  data,
  className,
}: {
  data: UsageRingData;
  className?: string;
}) {
  const { t } = useI18n();
  const limit = data.limit || 1;
  const pct = Math.min(100, Math.round((data.used / limit) * 100));
  const label = t(data.key);
  const strokeColor = RING_COLORS[data.key] || 'text-brand';

  // SVG circular progress calculation: r=13 -> circumference = 2 * PI * 13 ≈ 81.68
  const radius = 13;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div
      className={cn('flex items-center gap-2 select-none', className)}
      role="img"
      aria-label={`${label}: ${pct}%`}
    >
      <div className="relative grid h-7 w-7 shrink-0 place-items-center">
        <svg className="h-7 w-7 -rotate-90" viewBox="0 0 32 32">
          {/* Background track circle */}
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            strokeWidth="3.2"
            className="text-line dark:text-slate-700"
            stroke="currentColor"
          />
          {/* Progress circle */}
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            strokeWidth="3.2"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className={cn('transition-all duration-300', strokeColor)}
            stroke="currentColor"
          />
        </svg>
        <span className="absolute text-[8px] font-extrabold text-ink dark:text-slate-200">
          {pct}%
        </span>
      </div>
      <div className="hidden leading-tight sm:block">
        <small className="block text-[10px] font-medium text-ink-3 dark:text-slate-400">
          {label}
        </small>
        <b className="font-mono text-[11px] font-bold text-ink dark:text-slate-200">
          {fmtCompact(data.used)}/{fmtCompact(data.limit)}
        </b>
      </div>
    </div>
  );
}
